import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { IconButton } from './IconButton';

const VARIANT = {
  info: {
    box: 'bg-brand-50 text-brand-800 border-brand-200 dark:bg-brand-900 dark:text-brand-100 dark:border-brand-700',
    Icon: Info,
  },
  success: {
    box: 'bg-success-50 text-success-800 border-success-200 dark:bg-success-900 dark:text-success-100 dark:border-success-700',
    Icon: CheckCircle2,
  },
  warning: {
    box: 'bg-warning-50 text-warning-800 border-warning-200 dark:bg-warning-900 dark:text-warning-100 dark:border-warning-700',
    Icon: TriangleAlert,
  },
  error: {
    box: 'bg-danger-50 text-danger-800 border-danger-200 dark:bg-danger-900 dark:text-danger-100 dark:border-danger-700',
    Icon: AlertCircle,
  },
} as const;

export interface AlertProps {
  variant?: keyof typeof VARIANT;
  title?: string;
  /** e.g. a Retry button. */
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
  children: ReactNode;
}

/**
 * The persistent, in-page message: a failed load with a Retry, a read-only-mode
 * notice, a validation summary. Transient feedback is a Toast instead —
 * toasting a blocking error (it disappears before it's read) or pinning a
 * "Saved!" banner are the two usual mix-ups.
 */
export function Alert({
  variant = 'info',
  title,
  action,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
  children,
}: AlertProps) {
  const { box, Icon } = VARIANT[variant];

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={cn('flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-start', box, className)}
    >
      <Icon size={18} className="mt-0.5 shrink-0" aria-hidden />

      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-semibold">{title}</p>}
        <div className={cn(title && 'mt-0.5')}>{children}</div>
      </div>

      {action}

      {onDismiss && (
        <IconButton aria-label={dismissLabel} size="sm" onClick={onDismiss} className="-mt-1 -mr-1">
          <X size={16} />
        </IconButton>
      )}
    </div>
  );
}
